import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import Layout from "../../components/Layout";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost/securecontrol-backend";

// Palette de couleurs SecureControl
const COLORS = {
  primary: '#0B3C5D',     // Bleu Sécurité
  secondary: '#1E88C8',   // Bleu Tech
  accent: '#4FC3F7',      // Bleu Clair
  darkText: '#2E2E2E',    // Gris Foncé
  lightBg: '#F5F7FA',     // Gris Clair
  white: '#FFFFFF',
  danger: '#f44336',
  success: '#4caf50',
  warning: '#ff9800',
  info: '#2196f3',
  successLight: '#e8f5e9',
  dangerLight: '#ffebee',
  warningLight: '#fff3e0',
  infoLight: '#e3f2fd'
};

const priorityLabel = (priorite) => {
  switch (priorite?.toLowerCase()) {
    case 'critique':
      return { 
        text: "Critique", 
        style: {
          backgroundColor: '#ffebee',
          color: '#c62828'
        }
      };
    case 'haute':
      return { 
        text: "Haute", 
        style: {
          backgroundColor: '#fff3e0',
          color: '#f57c00'
        }
      };
    case 'moyenne':
      return { 
        text: "Moyenne", 
        style: {
          backgroundColor: '#fff8e1',
          color: '#ff8f00'
        }
      };
    case 'basse':
      return { 
        text: "Basse", 
        style: {
          backgroundColor: '#e8f5e9',
          color: '#2e7d32'
        }
      };
    default:
      return { 
        text: "Moyenne", 
        style: {
          backgroundColor: '#fff8e1',
          color: '#ff8f00'
        }
      };
  }
};

const statusLabel = (statut) => {
  switch (statut?.toLowerCase()) {
    case 'en_cours':
    case 'en cours':
      return { 
        text: "En cours", 
        style: {
          backgroundColor: '#e3f2fd',
          color: '#1565c0'
        }
      };
    case 'termine':
    case 'terminé':
    case 'terminée':
      return { 
        text: "Terminée", 
        style: {
          backgroundColor: '#e8f5e9',
          color: '#2e7d32'
        }
      };
    case 'en_attente':
    case 'en attente':
    default:
      return { 
        text: "En attente", 
        style: {
          backgroundColor: '#f5f5f5',
          color: '#616161'
        }
      };
  }
};

const formatDate = (dateString) => {
  if (!dateString || dateString === "0000-00-00" || dateString === "0000-00-00 00:00:00") {
    return "-";
  }
  const date = new Date(dateString);
  return date.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

const formatDateTime = (dateString) => {
  if (!dateString || dateString === "0000-00-00 00:00:00") return "-";
  const date = new Date(dateString);
  return date.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// Date locale du jour au format YYYY-MM-DD (pour défaut des filtres date) —
// new Date().toISOString() donne la date en UTC, ce qui décale le jour selon
// l'heure locale ; on reconstruit donc la date à partir des getters locaux.
const getTodayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const TaskList = () => {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [filteredTasks, setFilteredTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [serverError, setServerError] = useState("");
  const [infoMsg, setInfoMsg] = useState("");
  const [agents, setAgents] = useState([]);
  const [societeInfo, setSocieteInfo] = useState(null);

  // Logo + coordonnées de la société connectée, embarqués en en-tête des PDF exportés.
  useEffect(() => {
    const token = localStorage.getItem("access_token");
    if (!token) return;
    axios.get(`${API_BASE_URL}/societes/my_logo.php`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => {
        const infoLigne = [res.data?.address_societe, res.data?.tel_societe].filter(Boolean).join(" — ");
        setSocieteInfo({
          logo: res.data?.logo_base64 || null,
          nom: res.data?.nom_societe || null,
          infoLigne: infoLigne || null,
        });
      })
      .catch(() => {});
  }, []);
  
  // États pour les filtres
  const [filterAgent, setFilterAgent] = useState("");
  // Pas de filtre de date par défaut : une tâche fraîchement créée (ex. via le
  // bouton "traiter" du tableau de bord) pouvait ne jamais apparaître dans
  // cette liste tant que ses dates ne tombaient pas exactement sur la journée
  // en cours — l'utilisateur la voyait "disparaître" alors qu'elle existait
  // bien en base.
  const [filterDateDebut, setFilterDateDebut] = useState("");
  const [filterDateFin, setFilterDateFin] = useState("");
  const [filterPriorite, setFilterPriorite] = useState("");
  const [filterStatut, setFilterStatut] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  
  // États pour la pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Récupérer les infos utilisateur
  const [userInfo, setUserInfo] = useState(() => {
    try {
      const stored = localStorage.getItem("user");
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });

  const fetchTasks = useCallback(async () => {
    setLoading(true);
    setServerError("");
    setInfoMsg("");
    
    try {
      const token = localStorage.getItem("access_token");
      if (!token) {
        setServerError("Session expirée. Veuillez vous reconnecter.");
        setTimeout(() => navigate("/login"), 2000);
        return;
      }

      const url = `${API_BASE_URL}/taches/list.php`;
      
      const response = await axios.get(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });
      
      console.log("Réponse API Tâches:", response.data);
      
      if (response.data?.success) {
        // CORRECTION : L'API retourne les tâches dans 'taches'
        const rawData = response.data.taches || response.data.data || [];
        
        console.log("Données brutes reçues:", rawData);
        
        // Normaliser les données
        const normalizedData = rawData.map(task => {
          // Extraire le nom de l'agent depuis agent_info
          let agentName = 'Agent inconnu';
          let agentNom = '';
          let agentPrenom = '';
          
          if (task.agent_info && task.agent_info.nom_complet) {
            agentName = task.agent_info.nom_complet;
            // Tenter de séparer nom et prénom
            const parts = task.agent_info.nom_complet.split(' ');
            if (parts.length > 1) {
              agentNom = parts[0] || '';
              agentPrenom = parts.slice(1).join(' ') || '';
            } else {
              agentNom = task.agent_info.nom_complet;
            }
          } else if (task.agent_nom && task.agent_prenom) {
            agentName = `${task.agent_nom} ${task.agent_prenom}`.trim();
            agentNom = task.agent_nom;
            agentPrenom = task.agent_prenom;
          } else if (task.agent_nom) {
            agentName = task.agent_nom;
            agentNom = task.agent_nom;
          }
          
          return {
            ...task,
            id: task.id_tache || task.id,
            titre_tache: task.titre_tache || task.titre || '',
            description_tache: task.description_tache || task.description || '',
            date_debut: task.date_debut || '',
            date_fin: task.date_fin || '',
            priorite: task.priorite || 'moyenne',
            statut_tache: task.statut_tache || task.statut || 'en_attente',
            agent_name: agentName,
            agent_nom: agentNom,
            agent_prenom: agentPrenom,
            id_agent: task.id_agent || task.agent_id
          };
        });
        
        console.log("Données normalisées:", normalizedData);
        
        const validTasks = normalizedData.filter(t => t.id !== null && t.id !== undefined);
        setTasks(validTasks);
        setFilteredTasks(validTasks);
        
        if (validTasks.length === 0) {
          setInfoMsg("Aucune tâche trouvée.");
        } else {
          setInfoMsg(`${validTasks.length} tâche(s) chargée(s) avec succès.`);
        }
      } else {
        setServerError(response.data?.message || "Impossible de charger la liste des tâches.");
      }
    } catch (err) {
      handleFetchError(err);
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  const fetchAgents = useCallback(async () => {
    try {
      const token = localStorage.getItem("access_token");
      if (!token) return;

      const url = `${API_BASE_URL}/agents/list.php`;
      
      const response = await axios.get(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      });
      
      console.log("Réponse API Agents:", response.data); 
      
      if (response.data?.success) {
        const agentsList = response.data.agents || response.data.data || [];
        console.log("Liste des agents:", agentsList);
        
        const formattedAgents = agentsList.map(agent => {
          const agentName = agent.nom_complet || 
                           `${agent.nom || ''} ${agent.prenom || ''}`.trim() || 
                           agent.nom_agent || 
                           'Nom inconnu';
          
          return {
            id: agent.id_agent || agent.id,
            name: agentName,
            matricule: agent.matricule || agent.matricule_agent || ''
          };
        });
        
        console.log("Agents formatés:", formattedAgents);
        setAgents(formattedAgents);
      }
    } catch (err) {
      console.error("Erreur lors du chargement des agents:", err);
    }
  }, []);

  useEffect(() => {
    fetchTasks();
    fetchAgents();
  }, [fetchTasks, fetchAgents]);

  const handleFetchError = (err) => {
    if (err.response?.status === 401) {
      setServerError("Session expirée. Veuillez vous reconnecter.");
      localStorage.removeItem("access_token");
      setTimeout(() => navigate("/login"), 2000);
    } else if (err.response?.status === 403) {
      setServerError("Accès interdit. Vous n'avez pas les permissions nécessaires.");
    } else if (err.response?.status === 500) {
      setServerError("Erreur serveur. Veuillez contacter l'administrateur.");
    } else if (err.request) {
      setServerError("Serveur inaccessible. Vérifiez votre connexion.");
    } else {
      setServerError("Erreur technique lors du chargement des tâches.");
    }
  };

  useEffect(() => {
    if (tasks.length > 0) {
      let filtered = [...tasks];

      if (filterAgent) {
        filtered = filtered.filter(t => {
          const agentId = t.id_agent || t.agent_id;
          return agentId && agentId.toString() === filterAgent;
        });
      }

      if (filterDateDebut) {
        // Comparaison par jour (YYYY-MM-DD) plutôt que par timestamp complet :
        // une tâche dont la date de début tombe le jour du filtre mais avec une
        // heure > 00:00 était auparavant exclue à tort par une comparaison
        // d'horodatage strict.
        filtered = filtered.filter(t => (t.date_debut || "").slice(0, 10) >= filterDateDebut);
      }

      if (filterDateFin) {
        filtered = filtered.filter(t => (t.date_fin || "").slice(0, 10) <= filterDateFin);
      }

      if (filterPriorite) {
        filtered = filtered.filter(t => 
          t.priorite?.toLowerCase() === filterPriorite.toLowerCase()
        );
      }

      if (filterStatut) {
        filtered = filtered.filter(t => 
          t.statut_tache?.toLowerCase() === filterStatut.toLowerCase().replace(' ', '_')
        );
      }

      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        filtered = filtered.filter(t => {
          const titre = t.titre_tache?.toLowerCase() || '';
          const description = t.description_tache?.toLowerCase() || '';
          const agentName = t.agent_name?.toLowerCase() || '';
          
          return titre.includes(searchLower) ||
                 description.includes(searchLower) ||
                 agentName.includes(searchLower);
        });
      }

      setFilteredTasks(filtered);
      setCurrentPage(1);
      
      if (filtered.length === 0) {
        setInfoMsg("Aucune tâche ne correspond aux critères de recherche.");
      } else {
        setInfoMsg("");
      }
    }
  }, [tasks, filterAgent, filterDateDebut, filterDateFin, filterPriorite, filterStatut, searchTerm]);

  const refreshTasks = () => {
    fetchTasks();
    fetchAgents(); 
  };

  const handleViewDetails = (task) => {
    const taskId = task.id_tache || task.id;
    
    if (!taskId) {
      alert("ID de tâche invalide");
      return;
    }
    navigate(`/taches/${taskId}`);
  };

  const handleEditTask = (task) => {
    const taskId = task.id_tache || task.id;
    if (!taskId) {
      alert("ID de tâche invalide");
      return;
    }
    navigate(`/taches/edit/${taskId}`);
  };

  const getAgentName = (task) => {
    if (task.agent_info?.nom_complet) return task.agent_info.nom_complet;
    if (task.agent_name) return task.agent_name;
    if (task.agent_nom && task.agent_prenom) return `${task.agent_nom} ${task.agent_prenom}`;
    if (task.agent_nom) return task.agent_nom;
    return 'Agent inconnu';
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredTasks.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredTasks.length / itemsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(parseInt(e.target.value));
    setCurrentPage(1);
  };

  const generatePDFReport = () => {
    if (filteredTasks.length === 0) {
      alert("Aucune donnée à exporter avec les filtres actuels.");
      return;
    }

    const doc = new jsPDF('p', 'mm', 'a4');
    const pageWidth = doc.internal.pageSize.getWidth();

    // En-tête : logo à gauche, nom/adresse/tél. de la société alignés sur la
    // même ligne (à droite du logo), titre centré sous l'ensemble, puis
    // séparateur avant le reste du contenu.
    const logoSize = 24;
    const logoTop = 8;
    let logoBottom = logoTop;
    if (societeInfo?.logo) {
      const format = societeInfo.logo.startsWith('data:image/png') ? 'PNG'
        : societeInfo.logo.startsWith('data:image/webp') ? 'WEBP'
        : 'JPEG';
      try {
        doc.addImage(societeInfo.logo, format, 14, logoTop, logoSize, logoSize);
        logoBottom = logoTop + logoSize;
      } catch (e) {
        // Logo illisible (ex: SVG non supporté par jsPDF) : export sans logo plutôt que d'échouer.
      }
    }

    const infoX = 14 + logoSize + 6;
    let infoY = logoTop + 6;
    if (societeInfo?.nom) {
      doc.setFontSize(12);
      doc.setFont("helvetica", "bold");
      doc.text(societeInfo.nom, infoX, infoY);
      infoY += 5;
    }
    if (societeInfo?.infoLigne) {
      doc.setFontSize(8);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(100);
      doc.text(societeInfo.infoLigne, infoX, infoY);
      doc.setTextColor(0);
      infoY += 5;
    }

    let sousTitreY = Math.max(logoBottom, infoY) + 6;

    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text("Rapport des Tâches", pageWidth / 2, sousTitreY, { align: 'center' });
    sousTitreY += 5;

    const sepY = sousTitreY + 3;
    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.3);
    doc.line(14, sepY, pageWidth - 14, sepY);
    const yLeft = sepY + 7;

    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.text(`Date de génération: ${new Date().toLocaleDateString('fr-FR')}`, 14, yLeft);
    doc.text(`Heure: ${new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`, 14, yLeft + 5);

    const enAttenteCount = filteredTasks.filter(t => t.statut_tache?.toLowerCase() === 'en_attente').length;
    const enCoursCount = filteredTasks.filter(t => t.statut_tache?.toLowerCase() === 'en_cours').length;
    const termineCount = filteredTasks.filter(t => t.statut_tache?.toLowerCase() === 'termine').length;

    doc.text(`Total tâches: ${filteredTasks.length}`, 140, yLeft);
    doc.text(`En attente: ${enAttenteCount} | En cours: ${enCoursCount} | Terminées: ${termineCount}`, 140, yLeft + 5);

    let filtersInfo = "";
    if (filterAgent) {
      const agent = agents.find(a => a.id.toString() === filterAgent);
      filtersInfo += `Agent: ${agent?.name || filterAgent} `;
    }
    if (filterDateDebut) filtersInfo += `Début: ${formatDate(filterDateDebut)} `;
    if (filterDateFin) filtersInfo += `Fin: ${formatDate(filterDateFin)} `;
    if (filterPriorite) filtersInfo += `Priorité: ${filterPriorite} `;
    if (filterStatut) filtersInfo += `Statut: ${filterStatut} `;

    if (filtersInfo) {
      doc.setFontSize(8);
      doc.setTextColor(100, 100, 100);
      doc.text(`Filtres appliqués: ${filtersInfo}`, 14, yLeft + 12);
      doc.setTextColor(0, 0, 0);
    }

    if (searchTerm) {
      doc.setFontSize(8);
      doc.setTextColor(100, 100, 100);
      doc.text(`Recherche: "${searchTerm}"`, 14, filtersInfo ? yLeft + 17 : yLeft + 12);
      doc.setTextColor(0, 0, 0);
    }

    const tableData = filteredTasks.map((task, index) => {
      const priorite = priorityLabel(task.priorite);
      const statut = statusLabel(task.statut_tache);
      return [
        (index + 1).toString(),
        task.titre_tache,
        getAgentName(task),
        formatDateTime(task.date_debut),
        formatDateTime(task.date_fin),
        priorite.text,
        statut.text
      ];
    });

    autoTable(doc, {
      startY: filtersInfo || searchTerm ? yLeft + (searchTerm ? 22 : 17) : yLeft + 15,
      head: [
        ['#', 'Titre', 'Agent', 'Date début', 'Date fin', 'Priorité', 'Statut']
      ],
      body: tableData,
      theme: 'striped',
      headStyles: { 
        fillColor: [11, 60, 93], // COLORS.primary en RGB
        textColor: 255,
        fontStyle: 'bold',
        fontSize: 10
      },
      styles: {
        fontSize: 9,
        cellPadding: 3
      },
      columnStyles: {
        0: { cellWidth: 10, halign: 'center' },
        1: { cellWidth: 40 },
        2: { cellWidth: 35 },
        3: { cellWidth: 30 },
        4: { cellWidth: 30 },
        5: { cellWidth: 20, halign: 'center' },
        6: { cellWidth: 25, halign: 'center' }
      },
      margin: { left: 14, right: 14 },
      didDrawPage: function(data) {
        doc.setFontSize(8);
        doc.setTextColor(100, 100, 100);
        doc.text(
          `Page ${data.pageNumber}`,
          doc.internal.pageSize.width / 2,
          doc.internal.pageSize.height - 10,
          { align: 'center' }
        );
      }
    });

    const timestamp = new Date().toISOString().slice(0, 10).replace(/-/g, '');
    const fileName = `rapport_taches_${timestamp}.pdf`;
    doc.save(fileName);
  };

  const getUniquePriorites = () => {
    const priorites = tasks.map(t => t.priorite).filter(p => p);
    return [...new Set(priorites)].sort();
  };

  const getUniqueStatuts = () => {
    const statuts = tasks.map(t => t.statut_tache).filter(s => s);
    return [...new Set(statuts)].sort();
  };

  const resetFilters = () => {
    setFilterAgent("");
    setFilterDateDebut("");
    setFilterDateFin("");
    setFilterPriorite("");
    setFilterStatut("");
    setSearchTerm("");
  };

  const hasActiveFilters = () => {
    return filterAgent || filterDateDebut || filterDateFin || filterPriorite || filterStatut || searchTerm;
  };

  const handleCreateTask = () => {
    navigate("/taches/create");
  };

  // Fonction pour obtenir les initiales de l'agent
  const getAgentInitials = (agentName) => {
    if (!agentName) return '?';
    const parts = agentName.split(' ');
    if (parts.length >= 2) {
      return (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase();
    }
    return agentName.charAt(0).toUpperCase();
  };

  return (
    <Layout>
      <div className="pagetitle">
        <h1 style={{ color: COLORS.primary, fontWeight: '600' }}>Gestion des tâches</h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/dashboard" style={{ color: COLORS.primary }}>Tableau de bord</Link>
            </li>
            <li className="breadcrumb-item active" style={{ color: COLORS.secondary }}>Tâches</li>
          </ol>
        </nav>
      </div>

      <section className="section">
        <div className="card" style={{ 
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          border: 'none'
        }}>
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center mb-4">
              <div>
                <h5 className="card-title mb-1" style={{ 
                  color: COLORS.primary,
                  borderBottom: `2px solid ${COLORS.lightBg}`,
                  paddingBottom: '16px',
                  width: '100%'
                }}>
                  <div className="d-flex align-items-center">
                    <div className="rounded-circle d-inline-flex align-items-center justify-content-center me-3" style={{ 
                      width: '36px',
                      height: '36px',
                      backgroundColor: '#E3F2FD',
                      color: COLORS.primary
                    }}>
                      <i className="bi bi-list-task"></i>
                    </div>
                    Liste des tâches
                  </div>
                </h5>
                <p className="text-muted mb-0" style={{ fontSize: '0.9rem', color: '#6c757d' }}>
                  <i className="bi bi-info-circle me-1"></i>
                  Gérez les tâches des agents et suivez leur progression
                </p>
              </div>
              
              <div className="d-flex gap-2">
                
                
                {/* Bouton Nouvelle tâche */}
                {userInfo?.profile === 3 && (
                  <button
                    className="btn fw-semibold"
                    onClick={handleCreateTask}
                    style={{
                      color: COLORS.white,
                      backgroundColor: COLORS.primary,
                      borderColor: COLORS.primary,
                      borderRadius: '8px',
                      padding: '10px 24px',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    <i className="bi bi-plus me-2"></i>
                    Nouvelle tâche
                  </button>
                )}
                
                {/* Bouton Réinitialiser les filtres */}
                {hasActiveFilters() && (
                  <button
                    className="btn fw-semibold"
                    onClick={resetFilters}
                    title="Réinitialiser tous les filtres"
                    style={{
                      color: COLORS.primary,
                      borderColor: COLORS.primary,
                      backgroundColor: 'transparent',
                      borderRadius: '8px',
                      padding: '10px 20px',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = '#E3F2FD';
                      e.target.style.borderColor = COLORS.secondary;
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = 'transparent';
                      e.target.style.borderColor = COLORS.primary;
                    }}
                  >
                    <i className="bi bi-arrow-clockwise me-2"></i>
                    Réinitialiser
                  </button>
                )}
              </div>
            </div>

            {serverError && (
              <div className="alert alert-dismissible fade show mb-4" style={{ 
                backgroundColor: '#ffebee',
                borderColor: COLORS.danger,
                color: '#c62828',
                borderRadius: '8px',
                borderLeft: `4px solid ${COLORS.danger}`
              }}>
                <div className="d-flex align-items-center">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  <div className="flex-grow-1">{serverError}</div>
                  <button 
                    type="button" 
                    className="btn-close" 
                    onClick={() => setServerError("")}
                  ></button>
                </div>
              </div>
            )}
            
            {infoMsg && !loading && (
              <div className="alert alert-success alert-dismissible fade show mb-4" style={{ 
                backgroundColor: '#e8f5e9',
                borderColor: COLORS.success,
                color: '#2e7d32',
                borderRadius: '8px',
                borderLeft: `4px solid ${COLORS.success}`
              }}>
                <div className="d-flex align-items-center">
                  <i className="bi bi-info-circle me-2"></i>
                  <div className="flex-grow-1">
                    {infoMsg.split('\n').map((line, i) => (
                      <div key={i}>{line}</div>
                    ))}
                  </div>
                  <button type="button" className="btn-close" onClick={() => setInfoMsg("")}></button>
                </div>
              </div>
            )}

            {/* Filtres */}
            <div className="row mb-4">
              <div className="col-12">
                <div className="card" style={{ 
                  backgroundColor: '#E3F2FD',
                  border: 'none',
                  borderRadius: '8px'
                }}>
                  <div className="card-body py-3">
                    <div className="row g-3">
                      {/* Filtre Agent */}
                      <div className="col-md-3">
                        <div className="input-group">
                          <span className="input-group-text" style={{ 
                            backgroundColor: COLORS.lightBg,
                            borderColor: '#e0e0e0',
                            color: COLORS.secondary
                          }}>
                            <i className="bi bi-person"></i>
                          </span>
                          <select 
                            className="form-select"
                            value={filterAgent}
                            onChange={(e) => setFilterAgent(e.target.value)}
                            style={{
                              borderColor: '#e0e0e0',
                              color: COLORS.darkText
                            }}
                          >
                            <option value="">Tous les agents</option>
                            {agents.length > 0 ? (
                              agents.map((agent, index) => (
                                <option key={index} value={agent.id}>
                                  {agent.name} {agent.matricule ? `(${agent.matricule})` : ''}
                                </option>
                              ))
                            ) : (
                              <option value="" disabled>Aucun agent trouvé</option>
                            )}
                          </select>
                        </div>
                        <small className="form-text text-muted">
                          {agents.length} agent(s) disponible(s)
                        </small>
                      </div>
                      
                      {/* Filtre Date début */}
                      <div className="col-md-3">
                        <div className="input-group">
                          <span className="input-group-text" style={{ 
                            backgroundColor: COLORS.lightBg,
                            borderColor: '#e0e0e0',
                            color: COLORS.secondary
                          }}>
                            <i className="bi bi-calendar-plus"></i>
                          </span>
                          <input 
                            type="date"
                            className="form-control"
                            value={filterDateDebut}
                            onChange={(e) => setFilterDateDebut(e.target.value)}
                            style={{
                              borderColor: '#e0e0e0',
                              color: COLORS.darkText
                            }}
                          />
                        </div>
                      </div>

                      {/* Filtre Date fin */}
                      <div className="col-md-3">
                        <div className="input-group">
                          <span className="input-group-text" style={{ 
                            backgroundColor: COLORS.lightBg,
                            borderColor: '#e0e0e0',
                            color: COLORS.secondary
                          }}>
                            <i className="bi bi-calendar-minus"></i>
                          </span>
                          <input 
                            type="date"
                            className="form-control"
                            value={filterDateFin}
                            onChange={(e) => setFilterDateFin(e.target.value)}
                            style={{
                              borderColor: '#e0e0e0',
                              color: COLORS.darkText
                            }}
                          />
                        </div>
                      </div>

                      {/* Filtre Priorité */}
                      <div className="col-md-3">
                        <div className="input-group">
                          <span className="input-group-text" style={{ 
                            backgroundColor: COLORS.lightBg,
                            borderColor: '#e0e0e0',
                            color: COLORS.secondary
                          }}>
                            <i className="bi bi-exclamation-triangle"></i>
                          </span>
                          <select 
                            className="form-select"
                            value={filterPriorite}
                            onChange={(e) => setFilterPriorite(e.target.value)}
                            style={{
                              borderColor: '#e0e0e0',
                              color: COLORS.darkText
                            }}
                          >
                            <option value="">Toutes les priorités</option>
                            {getUniquePriorites().map((priorite, index) => (
                              <option key={index} value={priorite}>
                                {priorityLabel(priorite).text}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      {/* Filtre Statut */}
                      <div className="col-md-3">
                        <div className="input-group">
                          <span className="input-group-text" style={{ 
                            backgroundColor: COLORS.lightBg,
                            borderColor: '#e0e0e0',
                            color: COLORS.secondary
                          }}>
                            <i className="bi bi-funnel"></i>
                          </span>
                          <select 
                            className="form-select"
                            value={filterStatut}
                            onChange={(e) => setFilterStatut(e.target.value)}
                            style={{
                              borderColor: '#e0e0e0',
                              color: COLORS.darkText
                            }}
                          >
                            <option value="">Tous les statuts</option>
                            {getUniqueStatuts().map((statut, index) => (
                              <option key={index} value={statut}>
                                {statusLabel(statut).text}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      {/* Filtre Recherche */}
                      <div className="col-md-3">
                        <div className="input-group">
                          <span className="input-group-text" style={{ 
                            backgroundColor: COLORS.lightBg,
                            borderColor: '#e0e0e0',
                            color: COLORS.secondary
                          }}>
                            <i className="bi bi-search"></i>
                          </span>
                          <input 
                            type="text"
                            className="form-control"
                            placeholder="Rechercher..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            style={{
                              borderColor: '#e0e0e0',
                              color: COLORS.darkText
                            }}
                          />
                          {searchTerm && (
                            <button
                              type="button"
                              className="btn"
                              onClick={() => setSearchTerm("")}
                              title="Effacer la recherche"
                              style={{
                                backgroundColor: COLORS.lightBg,
                                borderColor: '#e0e0e0',
                                color: COLORS.darkText,
                                transition: 'all 0.3s ease'
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.backgroundColor = '#e0e0e0';
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.backgroundColor = COLORS.lightBg;
                              }}
                            >
                              <i className="bi bi-x"></i>
                            </button>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Résumé des filtres */}
                    <div className="mt-3 d-flex justify-content-between align-items-center">
                      <div className="text-muted small" style={{ color: COLORS.darkText }}>
                        <i className="bi bi-info-circle me-1"></i>
                        <span className="badge fw-semibold me-2" style={{ 
                          backgroundColor: COLORS.lightBg,
                          color: COLORS.primary,
                          padding: '4px 12px',
                          borderRadius: '6px'
                        }}>
                          {filteredTasks.length} tâche(s)
                          {filteredTasks.length !== tasks.length && 
                            ` sur ${tasks.length} au total`}
                        </span>
                        
                        {hasActiveFilters() && (
                          <span>
                            <i className="bi bi-funnel me-1"></i>
                            Filtres actifs:
                            {filterAgent && <span className="badge bg-info ms-1" style={{ backgroundColor: COLORS.info }}>Agent</span>}
                            {filterDateDebut && <span className="badge bg-info ms-1" style={{ backgroundColor: COLORS.info }}>Début</span>}
                            {filterDateFin && <span className="badge bg-info ms-1" style={{ backgroundColor: COLORS.info }}>Fin</span>}
                            {filterPriorite && <span className="badge bg-info ms-1" style={{ backgroundColor: COLORS.info }}>Priorité</span>}
                            {filterStatut && <span className="badge bg-info ms-1" style={{ backgroundColor: COLORS.info }}>Statut</span>}
                            {searchTerm && <span className="badge bg-info ms-1" style={{ backgroundColor: COLORS.info }}>Recherche</span>}
                          </span>
                        )}
                      </div>
                      
                      <div>
                        <button
                          className="btn fw-semibold"
                          onClick={generatePDFReport}
                          disabled={loading || filteredTasks.length === 0}
                          title="Générer un rapport PDF des tâches filtrées"
                          style={{
                            color: COLORS.white,
                            backgroundColor: COLORS.danger,
                            borderColor: COLORS.danger,
                            borderRadius: '8px',
                            padding: '8px 16px',
                            fontSize: '0.9rem',
                            transition: 'all 0.3s ease'
                          }}
                          onMouseEnter={(e) => {
                            if (!loading && filteredTasks.length > 0) {
                              e.target.style.backgroundColor = '#d32f2f';
                              e.target.style.borderColor = '#d32f2f';
                              e.target.style.transform = 'translateY(-2px)';
                              e.target.style.boxShadow = '0 4px 8px rgba(244, 67, 54, 0.2)';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!loading && filteredTasks.length > 0) {
                              e.target.style.backgroundColor = COLORS.danger;
                              e.target.style.borderColor = COLORS.danger;
                              e.target.style.transform = 'translateY(0)';
                              e.target.style.boxShadow = 'none';
                            }
                          }}
                        >
                          <i className="bi bi-file-earmark-pdf-fill me-2"></i>
                          Générer PDF
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border" role="status" style={{ color: COLORS.primary }}>
                  <span className="visually-hidden">Chargement...</span>
                </div>
                <p className="mt-2" style={{ color: COLORS.darkText }}>Chargement des tâches...</p>
              </div>
            ) : tasks.length === 0 ? (
              <div className="text-center py-5">
                <div className="mb-4">
                  <div className="rounded-circle d-flex align-items-center justify-content-center mx-auto" style={{ 
                    width: '80px',
                    height: '80px',
                    backgroundColor: '#F5F7FA',
                    color: '#B0BEC5'
                  }}>
                    <i className="bi bi-list-task fs-1"></i>
                  </div>
                </div>
                <h6 className="mb-3" style={{ color: COLORS.primary }}>Aucune tâche trouvée</h6>
                <p className="text-muted" style={{ color: COLORS.darkText, maxWidth: '500px', margin: '0 auto' }}>
                  Aucune tâche n'a été créée pour le moment.
                </p>
                {userInfo?.profile === 3 && (
                  <button
                    onClick={handleCreateTask}
                    className="btn fw-semibold mt-3"
                    style={{
                      color: COLORS.white,
                      backgroundColor: COLORS.primary,
                      borderColor: COLORS.primary,
                      borderRadius: '8px',
                      padding: '10px 24px',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = COLORS.secondary;
                      e.target.style.borderColor = COLORS.secondary;
                      e.target.style.transform = 'translateY(-2px)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = COLORS.primary;
                      e.target.style.borderColor = COLORS.primary;
                      e.target.style.transform = 'translateY(0)';
                    }}
                  >
                    <i className="bi bi-plus-circle me-2"></i>
                    Créer une tâche
                  </button>
                )}
              </div>
            ) : (
              <>
                {/* Affichage des résultats filtrés */}
                {filteredTasks.length === 0 ? (
                  <div className="text-center py-5">
                    <div className="mb-4">
                      <div className="rounded-circle d-flex align-items-center justify-content-center mx-auto" style={{ 
                        width: '80px',
                        height: '80px',
                        backgroundColor: '#F5F7FA',
                        color: '#B0BEC5'
                      }}>
                        <i className="bi bi-search fs-1"></i>
                      </div>
                    </div>
                    <h6 className="mb-3" style={{ color: COLORS.primary }}>Aucun résultat</h6>
                    <p className="text-muted" style={{ color: COLORS.darkText, maxWidth: '500px', margin: '0 auto' }}>
                      Aucune tâche ne correspond aux critères de recherche.
                    </p>
                    <button
                      className="btn fw-semibold mt-3"
                      onClick={resetFilters}
                      style={{
                        color: COLORS.primary,
                        borderColor: COLORS.primary,
                        backgroundColor: 'transparent',
                        borderRadius: '8px',
                        padding: '10px 24px',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = COLORS.primary;
                        e.target.style.color = COLORS.white;
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = 'transparent';
                        e.target.style.color = COLORS.primary;
                      }}
                    >
                      <i className="bi bi-x-lg me-2"></i> 
                      Effacer les filtres
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="table-responsive">
                      <table className="table table-hover mb-0">
                        <thead>
                          <tr style={{ 
                            backgroundColor: COLORS.lightBg,
                            borderBottom: `2px solid ${COLORS.accent}`
                          }}>
                            <th style={{ color: COLORS.primary, fontWeight: '600', padding: '16px' }}>Titre</th>
                            <th style={{ color: COLORS.primary, fontWeight: '600', padding: '16px' }}>Agent</th>
                            <th style={{ color: COLORS.primary, fontWeight: '600', padding: '16px' }}>Date début</th>
                            <th style={{ color: COLORS.primary, fontWeight: '600', padding: '16px' }}>Date fin</th>
                            <th style={{ color: COLORS.primary, fontWeight: '600', padding: '16px' }}>Priorité</th>
                            <th style={{ color: COLORS.primary, fontWeight: '600', padding: '16px' }}>Statut</th>
                            <th style={{ color: COLORS.primary, fontWeight: '600', padding: '16px', textAlign: 'center' }}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {currentItems.map((task, index) => {
                            const priorite = priorityLabel(task.priorite);
                            const statut = statusLabel(task.statut_tache);
                            const rowId = task.id_tache || task.id || `row-${index}`;
                            const agentName = getAgentName(task);
                            const isLate = new Date(task.date_fin) < new Date() && task.statut_tache !== 'termine';

                            return (
                              <tr key={rowId} style={{ 
                                borderBottom: `1px solid ${COLORS.lightBg}`,
                                ...(isLate && { backgroundColor: '#fff8e1' })
                              }}>
                                <td style={{ padding: '16px' }}>
                                  <div className="d-flex align-items-center"> 
                                    <div>
                                      <div style={{ color: COLORS.darkText, fontWeight: '500' }}>
                                        {task.titre_tache || 'Sans titre'}
                                      </div>
                                      <small className="text-muted" style={{ fontSize: '0.8rem' }}>
                                        {task.description_tache?.substring(0, 50) || 'Aucune description'}...
                                      </small>
                                    </div>
                                  </div>
                                </td>
                                
                                <td style={{ padding: '16px' }}>
                                  <div className="d-flex align-items-center">
                                    <div className="me-3">
                                      <div 
                                        className="text-white rounded-circle d-flex align-items-center justify-content-center fw-semibold"
                                        style={{ 
                                          width: '40px', 
                                          height: '40px', 
                                          fontSize: '14px',
                                          backgroundColor: COLORS.primary,
                                          boxShadow: '0 2px 4px rgba(11, 60, 93, 0.1)'
                                        }}
                                        title="Agent"
                                      >
                                        {getAgentInitials(agentName)}
                                      </div>
                                    </div>
                                    <div>
                                      <div style={{ color: COLORS.darkText, fontWeight: '500' }}>
                                        {agentName || 'Agent inconnu'}
                                      </div>
                                      
                                    </div>
                                  </div>
                                </td>
                                
                                <td style={{ padding: '16px' }}>
                                  <div>
                                    <div style={{ color: COLORS.darkText, fontWeight: '500' }}>
                                      {formatDate(task.date_debut)}
                                    </div>
                                    <small className="text-muted" style={{ fontSize: '0.8rem' }}>
                                      {task.date_debut && new Date(task.date_debut).toLocaleTimeString('fr-FR', { 
                                        hour: '2-digit', 
                                        minute: '2-digit' 
                                      })}
                                    </small>
                                  </div>
                                </td>
                                
                                <td style={{ padding: '16px' }}>
                                  <div>
                                    <div style={{ 
                                      color: isLate ? '#f44336' : COLORS.darkText, 
                                      fontWeight: '500' 
                                    }}>
                                      {formatDate(task.date_fin)}
                                      {isLate && (
                                        <i className="bi bi-exclamation-triangle ms-1" style={{ color: '#f44336' }}></i>
                                      )}
                                    </div>
                                    <small className="text-muted" style={{ fontSize: '0.8rem' }}>
                                      {task.date_fin && new Date(task.date_fin).toLocaleTimeString('fr-FR', { 
                                        hour: '2-digit', 
                                        minute: '2-digit' 
                                      })}
                                    </small>
                                  </div>
                                </td>
                                
                                <td style={{ padding: '16px' }}>
                                  <span className="badge fw-semibold" style={{ 
                                    ...priorite.style,
                                    padding: '8px 16px',
                                    borderRadius: '6px',
                                    fontSize: '0.85rem'
                                  }}>
                                    {priorite.text}
                                  </span>
                                </td>
                                
                                <td style={{ padding: '16px' }}>
                                  <span className="badge fw-semibold" style={{ 
                                    ...statut.style,
                                    padding: '8px 16px',
                                    borderRadius: '6px',
                                    fontSize: '0.85rem'
                                  }}>
                                    {statut.text}
                                  </span>
                                </td>
                                
                                <td style={{ padding: '16px', textAlign: 'center' }}>
                                  <div className="d-flex gap-2 justify-content-center">
                                    {/* Bouton Voir détails */}
                                    <button
                                      className="btn btn-outline-success fw-semibold"
                                      onClick={() => handleViewDetails(task)}
                                      title="Voir détails"
                                    >
                                      <i className="bi bi-eye"></i>
                                    </button>
                                    
                                    {/* Bouton Modifier (uniquement pour gestionnaires) */}
                                    {userInfo?.profile === 3 && (
                                      <button
                                        className="btn btn-outline-info fw-semibold"
                                        onClick={() => handleEditTask(task)}
                                        title="Modifier"
                                      >
                                        <i className="bi bi-pencil"></i>
                                      </button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                      <div className="d-flex justify-content-between align-items-center mt-4">
                        <div className="d-flex align-items-center">
                          <span className="me-2" style={{ color: COLORS.darkText }}>Afficher :</span>
                          <select 
                            className="form-select form-select-sm w-auto"
                            value={itemsPerPage}
                            onChange={handleItemsPerPageChange}
                            style={{
                              borderColor: '#e0e0e0',
                              color: COLORS.darkText,
                              borderRadius: '6px'
                            }}
                          >
                            <option value="5">5</option>
                            <option value="10">10</option>
                            <option value="25">25</option>
                            <option value="50">50</option>
                            <option value="100">100</option>
                          </select>
                          <span className="ms-3" style={{ color: COLORS.darkText }}>
                            <span className="badge fw-semibold me-2" style={{ 
                              backgroundColor: '#E3F2FD',
                              color: COLORS.primary,
                              padding: '4px 12px',
                              borderRadius: '6px'
                            }}>
                              {filteredTasks.length} tâche(s)
                              {filteredTasks.length !== tasks.length ? ' (filtrées)' : ''}
                            </span>
                          </span>
                        </div>
                        
                        <nav>
                          <ul className="pagination pagination-sm mb-0">
                            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                              <button 
                                className="page-link fw-semibold"
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={currentPage === 1}
                                style={{
                                  color: currentPage === 1 ? '#B0BEC5' : COLORS.primary,
                                  borderColor: '#e0e0e0',
                                  padding: '6px 12px',
                                  borderRadius: '6px',
                                  margin: '0 2px'
                                }}
                              >
                                <i className="bi bi-chevron-left"></i>
                              </button>
                            </li>
                            
                            {Array.from({ length: totalPages }, (_, i) => i + 1)
                              .filter(page => {
                                if (totalPages <= 7) return true;
                                return page === 1 || 
                                       page === totalPages || 
                                       (page >= currentPage - 1 && page <= currentPage + 1);
                              })
                              .map((page, index, array) => {
                                if (index > 0 && page > array[index - 1] + 1) {
                                  return (
                                    <React.Fragment key={`ellipsis-${page}`}>
                                      <li className="page-item disabled">
                                        <span className="page-link" style={{ borderColor: '#e0e0e0' }}>...</span>
                                      </li>
                                      <li className={`page-item ${currentPage === page ? 'active' : ''}`}>
                                        <button 
                                          className="page-link fw-semibold"
                                          onClick={() => handlePageChange(page)}
                                          style={{
                                            backgroundColor: currentPage === page ? COLORS.primary : 'transparent',
                                            borderColor: currentPage === page ? COLORS.primary : '#e0e0e0',
                                            color: currentPage === page ? COLORS.white : COLORS.primary,
                                            padding: '6px 12px',
                                            borderRadius: '6px',
                                            margin: '0 2px',
                                            transition: 'all 0.3s ease'
                                          }}
                                          onMouseEnter={(e) => {
                                            if (currentPage !== page) {
                                              e.target.style.backgroundColor = '#E3F2FD';
                                              e.target.style.borderColor = COLORS.secondary;
                                            }
                                          }}
                                          onMouseLeave={(e) => {
                                            if (currentPage !== page) {
                                              e.target.style.backgroundColor = 'transparent';
                                              e.target.style.borderColor = '#e0e0e0';
                                            }
                                          }}
                                        >
                                          {page}
                                        </button>
                                      </li>
                                    </React.Fragment>
                                  );
                                }
                                
                                return (
                                  <li key={page} className={`page-item ${currentPage === page ? 'active' : ''}`}>
                                    <button 
                                      className="page-link fw-semibold"
                                      onClick={() => handlePageChange(page)}
                                      style={{
                                        backgroundColor: currentPage === page ? COLORS.primary : 'transparent',
                                        borderColor: currentPage === page ? COLORS.primary : '#e0e0e0',
                                        color: currentPage === page ? COLORS.white : COLORS.primary,
                                        padding: '6px 12px',
                                        borderRadius: '6px',
                                        margin: '0 2px',
                                        transition: 'all 0.3s ease'
                                      }}
                                      onMouseEnter={(e) => {
                                        if (currentPage !== page) {
                                          e.target.style.backgroundColor = '#E3F2FD';
                                          e.target.style.borderColor = COLORS.secondary;
                                        }
                                      }}
                                      onMouseLeave={(e) => {
                                        if (currentPage !== page) {
                                          e.target.style.backgroundColor = 'transparent';
                                          e.target.style.borderColor = '#e0e0e0';
                                        }
                                      }}
                                    >
                                      {page}
                                    </button>
                                  </li>
                                );
                              })}
                            
                            <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                              <button 
                                className="page-link fw-semibold"
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={currentPage === totalPages}
                                style={{
                                  color: currentPage === totalPages ? '#B0BEC5' : COLORS.primary,
                                  borderColor: '#e0e0e0',
                                  padding: '6px 12px',
                                  borderRadius: '6px',
                                  margin: '0 2px'
                                }}
                              >
                                <i className="bi bi-chevron-right"></i>
                              </button>
                            </li>
                          </ul>
                        </nav>
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </section>

      {/* Styles CSS additionnels */}
      <style>{`
        .pagetitle h1,
        .card-title,
        .btn,
        .page-link,
        th {
          font-family: 'Inter', sans-serif;
        }
        
        .card-body,
        .table,
        td,
        small,
        .alert {
          font-family: 'Roboto', sans-serif;
        }
        
        .btn:focus,
        .form-control:focus,
        .page-link:focus,
        .form-select:focus {
          outline: none;
          box-shadow: 0 0 0 0.2rem rgba(30, 136, 200, 0.25);
        }
        
        .btn,
        .badge,
        .page-link {
          transition: all 0.2s ease-in-out;
        }
        
        .btn:disabled,
        .page-link:disabled {
          cursor: not-allowed;
          opacity: 0.6;
        }
        
        .form-control:focus,
        .form-select:focus {
          border-color: ${COLORS.secondary};
          box-shadow: 0 0 0 0.2rem rgba(30, 136, 200, 0.25);
        }
        
        .table-hover tbody tr:hover {
          background-color: ${COLORS.lightBg};
          transition: background-color 0.2s ease;
        }
        
        .badge {
          font-weight: 500;
        }
        
        @media (max-width: 768px) {
          .card {
            margin: 0 -16px;
            border-radius: 0 !important;
          }
          
          .pagetitle h1 {
            font-size: 1.5rem;
          }
          
          .d-flex.justify-content-between {
            flex-direction: column;
            gap: 16px;
          }
          
          .d-flex.justify-content-between > div {
            width: 100%;
          }
          
          .btn {
            width: 100%;
            margin-bottom: 8px;
          }
          
          .table-responsive {
            border: none;
          }
          
          .table {
            font-size: 0.875rem;
          }
          
          .table th,
          .table td {
            padding: 12px 8px !important;
          }
          
          .row.g-3 {
            margin-bottom: 8px;
          }
          
          .row.g-3 > div {
            margin-bottom: 8px;
          }
        }
        
        /* Amélioration de la lisibilité des tables */
        .table th {
          text-transform: uppercase;
          letter-spacing: 0.5px;
          font-size: 0.85rem;
        }
        
        .table td {
          vertical-align: middle;
        }
        
        /* Animation pour les badges */
        .badge:hover {
          transform: translateY(-1px);
        }
        
        /* Styles pour les badges de statut */
        .badge.bg-success {
          background-color: ${COLORS.success} !important;
        }
        
        .badge.bg-warning {
          background-color: ${COLORS.warning} !important;
        }
        
        .badge.bg-info {
          background-color: ${COLORS.info} !important;
        }
      `}</style>
    </Layout>
  );
};

export default TaskList;